import { FC, useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  Tag,
  Button,
  Link as CarbonLink,
  DatePicker,
  DatePickerInput,
  RadioButtonGroup,
  RadioButton,
} from '@carbon/react';
import {
  View,
  ArrowLeft,
  Money,
  ChartLineData,
  ChartColumn,
  CheckmarkFilled,
  Time,
  Settings,
} from '@carbon/react/icons';
import routes from '~/routes';
import { PageHeader } from 'components/page-header';
import { DataTable, FilterSection } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { formatNumber } from 'utils/number';
import { fetchAgentRuns } from './api';
import { useAgentResource } from './use-agent-resource';
import { RunDetailSidePanel } from './run-detail-panel';
import { ProcessStepsSidePanel } from './process-steps-panel';
import { AgentConfigSidePanel } from './agent-config-panel';
import { DurationHistogram } from './duration-histogram';
import { MetricGrid, MetricCard, STAT_COLORS } from '../kpi-cards';
import { fromDateParam, toDateParam } from 'utils/dates';
import {
  formatCost,
  formatDuration,
  formatRunCost,
  statusTagColor,
  titleize,
} from '../format';
import type { AgentRun, DurationBucket } from './types';
import styles from './page.module.scss';

// Headline totals read cleaner at 2 decimals.
const formatMoney = (value: number): string => formatCost(value);

type SelectOption = { id: string; label: string };

const STATUS_SELECT_OPTIONS: SelectOption[] = [
  { id: 'all', label: 'All Statuses' },
  { id: 'processed', label: 'Processed' },
  { id: 'failed', label: 'Failed' },
  { id: 'processing', label: 'Processing' },
  { id: 'pending', label: 'Pending' },
];

const PAGE_SIZE = 100;

// ── Server-side query state (single reducer) ──
// Grouped so the "any filter/sort/bucket/date change resets to page 1"
// invariant is enforced structurally by the reducer rather than by scattered
// setPage(1) calls. Pagination is the only action that changes page without a
// reset (it IS the page change).
interface QueryState {
  page: number;
  pageSize: number;
  statusFilter: string;
  modelFilter: string;
  startDate: string | null;
  endDate: string | null;
  durationBucket: DurationBucket | null;
  sort: string | null;
  dir: 'asc' | 'desc';
}

const INITIAL_QUERY: QueryState = {
  page: 1,
  pageSize: PAGE_SIZE,
  statusFilter: 'all',
  modelFilter: 'all',
  startDate: null,
  endDate: null,
  durationBucket: null,
  sort: 'createdAt',
  dir: 'desc',
};

type QueryAction =
  | { type: 'setStatus'; value: string }
  | { type: 'setModel'; value: string }
  | { type: 'setDates'; start: string | null; end: string | null }
  | { type: 'setBucket'; bucket: DurationBucket | null }
  | { type: 'setSort'; sort: string | null; dir: 'asc' | 'desc' }
  | { type: 'setPagination'; page: number; pageSize: number }
  | { type: 'resetFilters' };

const queryReducer = (state: QueryState, action: QueryAction): QueryState => {
  switch (action.type) {
    case 'setStatus':
      return { ...state, statusFilter: action.value, page: 1 };
    case 'setModel':
      return { ...state, modelFilter: action.value, page: 1 };
    case 'setDates':
      return {
        ...state,
        startDate: action.start,
        endDate: action.end,
        page: 1,
      };
    case 'setBucket':
      return { ...state, durationBucket: action.bucket, page: 1 };
    case 'setSort':
      return { ...state, sort: action.sort, dir: action.dir, page: 1 };
    case 'setPagination':
      return { ...state, page: action.page, pageSize: action.pageSize };
    case 'resetFilters':
      return {
        ...state,
        statusFilter: 'all',
        modelFilter: 'all',
        startDate: null,
        endDate: null,
        durationBucket: null,
        page: 1,
      };
    default:
      return state;
  }
};

const runForHref = (run: AgentRun): string | null => {
  if (run.runForId === null) return null;
  if (run.runForKind === 'entity') {
    return routes.entityPath.replace(':id', String(run.runForId));
  }
  if (run.runForKind === 'document') {
    return routes.documentPath.replace(':id', String(run.runForId));
  }
  return null;
};

const columnHelper = createColumnHelper<AgentRun>();

// Columns whose `id` matches a server-side sortable key (Finder::SORT_SQL).
// runFor (entity name) and cost (computed per-model) aren't sortable in SQL.
const buildColumns = (
  onView: (run: AgentRun) => void,
  onOpenProcess: (processId: number) => void
) => [
  columnHelper.accessor('workflowProcessId', {
    id: 'id',
    header: 'Workflow Process ID',
    size: 160,
    enableColumnFilter: false,
    cell: (info) => (
      <button
        type="button"
        className={styles.runId}
        onClick={() => onOpenProcess(info.getValue())}
        title="View workflow process & steps"
      >
        {info.getValue()}
      </button>
    ),
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Date',
    size: 180,
    meta: { type: 'datetime' },
  }),
  columnHelper.accessor('runFor', {
    id: 'runFor',
    header: 'Run For',
    size: 240,
    enableSorting: false,
    meta: { type: 'text' },
    cell: (info) => {
      const label = info.getValue();
      if (!label) return <span className={styles.muted}>—</span>;
      const href = runForHref(info.row.original);
      return href ? (
        <CarbonLink href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </CarbonLink>
      ) : (
        label
      );
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 120,
    cell: (info) => {
      const value = info.getValue();
      return (
        <Tag type={statusTagColor(value)} size="sm">
          {value}
        </Tag>
      );
    },
  }),
  columnHelper.accessor('durationSeconds', {
    id: 'durationSeconds',
    header: 'Duration',
    size: 160,
    meta: { type: 'duration' },
  }),
  columnHelper.accessor((row) => row.tokens.total, {
    id: 'tokens',
    header: 'Tokens',
    size: 130,
    meta: { type: 'integer' },
    cell: (info) => {
      const t = info.row.original.tokens;
      return (
        <span
          title={`input ${t.input} · output ${t.output} · cache write ${t.cacheWrite} · cache read ${t.cacheRead}`}
        >
          {formatNumber({
            value: t.total,
            minPrecision: 0,
            includeCommas: true,
          })}
        </span>
      );
    },
  }),
  columnHelper.accessor('cost', {
    id: 'cost',
    header: 'Cost',
    size: 130,
    enableSorting: false,
    meta: { type: 'currency' },
    cell: (info) => <strong>{formatRunCost(info.getValue())}</strong>,
  }),
  columnHelper.accessor('model', {
    id: 'model',
    header: 'Model',
    size: 220,
    meta: { type: 'text' },
    cell: (info) => info.getValue() || <span className={styles.muted}>—</span>,
  }),
  columnHelper.accessor('error', {
    id: 'error',
    header: 'Error',
    size: 260,
    enableSorting: false,
    meta: { type: 'text' },
    cell: (info) => {
      const value = info.getValue();
      return value ? (
        <span className={styles.errorText}>{value}</span>
      ) : (
        <span className={styles.muted}>—</span>
      );
    },
  }),
  columnHelper.display({
    id: 'details',
    header: 'Details',
    size: 110,
    enableSorting: false,
    enableColumnFilter: false,
    cell: (info) => (
      <Button
        kind="ghost"
        size="sm"
        renderIcon={View}
        onClick={() => onView(info.row.original)}
      >
        View
      </Button>
    ),
  }),
];

export const AgentCostDetailPage: FC = () => {
  const history = useHistory();
  const { agentType } = useParams<{ agentType: string }>();

  // Independent UI state (not part of the server query).
  const [selectedRun, setSelectedRun] = useState<AgentRun | null>(null);
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(
    null
  );
  const [configOpen, setConfigOpen] = useState(false);
  const [histogramOpen, setHistogramOpen] = useState(true);

  // Server-side query state — one reducer so filter/sort/bucket/date changes
  // structurally reset to page 1 (see queryReducer).
  const [queryState, dispatch] = useReducer(queryReducer, INITIAL_QUERY);
  const {
    page,
    pageSize,
    statusFilter,
    modelFilter,
    startDate,
    endDate,
    durationBucket,
    sort,
    dir,
  } = queryState;

  const activeFilterCount =
    (statusFilter !== 'all' ? 1 : 0) +
    (modelFilter !== 'all' ? 1 : 0) +
    (startDate || endDate ? 1 : 0) +
    (durationBucket ? 1 : 0);

  const resetFilters = () => dispatch({ type: 'resetFilters' });

  const query = useMemo(
    () => ({
      page,
      pageSize,
      status: statusFilter === 'all' ? null : statusFilter,
      model: modelFilter === 'all' ? null : modelFilter,
      start: startDate,
      end: endDate,
      minDuration: durationBucket ? durationBucket.min : null,
      maxDuration: durationBucket ? durationBucket.max : null,
      sort,
      dir,
    }),
    [
      page,
      pageSize,
      statusFilter,
      modelFilter,
      startDate,
      endDate,
      durationBucket,
      sort,
      dir,
    ]
  );

  const {
    data: response,
    loading,
    error,
  } = useAgentResource(
    useCallback(
      (controller: AbortController) =>
        fetchAgentRuns(agentType, query, controller),
      [agentType, query]
    ),
    [agentType, query]
  );

  // The hook clears `response` to null while a refetch is in flight; retain the
  // last successful payload so the table keeps showing its (dimmed) rows during
  // filter/sort/page changes — matching the DataTable's stale-rows loading model.
  const lastResponseRef = useRef(response);
  if (response) lastResponseRef.current = response;
  const effectiveResponse = response ?? lastResponseRef.current;

  const data = effectiveResponse?.data ?? [];
  const total = effectiveResponse?.total ?? 0;
  const summary = effectiveResponse?.summary ?? null;
  const models = useMemo(
    () => effectiveResponse?.models ?? [],
    [effectiveResponse]
  );

  const columns = useMemo(
    () =>
      buildColumns(
        (run) => {
          setSelectedProcessId(null);
          setSelectedRun(run);
        },
        (processId) => {
          setSelectedRun(null);
          setSelectedProcessId(processId);
        }
      ),
    []
  );

  const modelSelectOptions = useMemo<SelectOption[]>(
    () => [
      { id: 'all', label: 'All Models' },
      ...models.map((m) => ({ id: m, label: m })),
    ],
    [models]
  );

  const handlePagination = ({ page: p, pageSize: ps }: TableState) =>
    dispatch({ type: 'setPagination', page: p, pageSize: ps });

  const selectBucket = (bucket: DurationBucket | null) =>
    dispatch({ type: 'setBucket', bucket });

  const handleSort = ({ sorting }: TableState) => {
    if (sorting.length === 0) {
      dispatch({ type: 'setSort', sort: 'createdAt', dir: 'desc' });
    } else {
      dispatch({
        type: 'setSort',
        sort: sorting[0].id,
        dir: sorting[0].desc ? 'desc' : 'asc',
      });
    }
  };

  const backButton = (
    <Button
      kind="ghost"
      size="sm"
      renderIcon={ArrowLeft}
      onClick={() => history.goBack()}
    >
      Back
    </Button>
  );

  const configButton = (
    <Button
      kind="tertiary"
      size="sm"
      renderIcon={Settings}
      onClick={() => setConfigOpen(true)}
    >
      Config
    </Button>
  );

  const headerActions = (
    <div className={styles.headerActions}>
      {summary && (
        <div className={styles.headerPills}>
          <Tag type="blue" size="sm">
            {formatNumber({
              value: summary.runs,
              minPrecision: 0,
              includeCommas: true,
            })}{' '}
            runs
          </Tag>
          <Tag type="green" size="sm">
            {formatNumber({
              value: summary.successRate,
              minPrecision: 1,
              maxPrecision: 1,
            })}
            % ok
          </Tag>
          {summary.failed > 0 && (
            <Tag type="red" size="sm">
              {formatNumber({
                value: summary.failed,
                minPrecision: 0,
                includeCommas: true,
              })}{' '}
              failed
            </Tag>
          )}
        </div>
      )}
      {configButton}
      {backButton}
    </div>
  );

  // Left filter nav, rendered inside the DataTable's built-in panel.
  const filterControls = (
    <div className={styles.filterPanelBody}>
      {activeFilterCount > 0 && (
        <Button
          kind="ghost"
          size="sm"
          className={styles.resetBtn}
          onClick={resetFilters}
        >
          Reset filters
        </Button>
      )}
      <FilterSection title="Date range">
        <DatePicker
          datePickerType="range"
          dateFormat="m/d/Y"
          value={[fromDateParam(startDate), fromDateParam(endDate)]}
          onChange={(dates: Date[]) => {
            // Only commit a complete range. Dispatching on the first (partial)
            // click would feed a 1-element value back into the controlled
            // picker mid-selection and reset flatpickr's range state, so the
            // second click could never finish the range. Matches the summary
            // page's DateRangeFilter.
            if (dates.length === 2) {
              dispatch({
                type: 'setDates',
                start: toDateParam(dates[0]),
                end: toDateParam(dates[1]),
              });
            }
          }}
        >
          <DatePickerInput
            id="agent-runs-start-date"
            labelText="From"
            placeholder="mm/dd/yyyy"
            size="sm"
          />
          <DatePickerInput
            id="agent-runs-end-date"
            labelText="To"
            placeholder="mm/dd/yyyy"
            size="sm"
          />
        </DatePicker>
      </FilterSection>
      <FilterSection title="Status">
        <RadioButtonGroup
          name="agent-runs-status"
          orientation="vertical"
          valueSelected={statusFilter}
          onChange={(v) => dispatch({ type: 'setStatus', value: String(v) })}
        >
          {STATUS_SELECT_OPTIONS.map((o) => (
            <RadioButton key={o.id} labelText={o.label} value={o.id} />
          ))}
        </RadioButtonGroup>
      </FilterSection>
      <FilterSection title="Model" defaultExpanded={false}>
        <RadioButtonGroup
          name="agent-runs-model"
          orientation="vertical"
          valueSelected={modelFilter}
          onChange={(v) => dispatch({ type: 'setModel', value: String(v) })}
        >
          {modelSelectOptions.map((o) => (
            <RadioButton key={o.id} labelText={o.label} value={o.id} />
          ))}
        </RadioButtonGroup>
      </FilterSection>
    </div>
  );

  // Run-duration distribution, injected between the table toolbar and the rows
  // (matches the AFE Catalog map pattern). Toggled from a toolbar action.
  const histogramPanel = (
    <div className={styles.histogramCard}>
      <div className={styles.histogramHeader}>
        <span className={styles.sectionTitle}>Run duration distribution</span>
        {durationBucket && (
          <Tag type="blue" size="sm" filter onClose={() => selectBucket(null)}>
            {durationBucket.label}
          </Tag>
        )}
      </div>
      <DurationHistogram
        agentType={agentType}
        filters={{
          status: query.status,
          model: query.model,
          start: query.start,
          end: query.end,
        }}
        selectedLabel={durationBucket?.label ?? null}
        onSelectBucket={selectBucket}
      />
    </div>
  );

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title={titleize(agentType)}
        subtitle="Per-run cost and detail — click View on a row for prompt, thinking, tools, and output."
        actions={headerActions}
      />
      {summary && !error && (
        <div className={styles.statArea}>
          <MetricGrid columns={4}>
            <MetricCard
              index={0}
              accent={STAT_COLORS.cost}
              icon={<Money size={20} />}
              label="Total Cost"
              value={formatMoney(summary.cost)}
              delta={
                <span className={styles.statSub}>
                  {formatNumber({
                    value: summary.costPerRun,
                    minPrecision: 4,
                    maxPrecision: 4,
                    style: 'currency',
                    includeCommas: true,
                  })}{' '}
                  / run
                </span>
              }
            />
            <MetricCard
              index={1}
              accent={STAT_COLORS.runs}
              icon={<ChartLineData size={20} />}
              label="Runs"
              value={formatNumber({
                value: summary.runs,
                minPrecision: 0,
                includeCommas: true,
              })}
              delta={
                <span className={styles.statSub}>
                  {summary.success} ok · {summary.failed} failed
                </span>
              }
            />
            <MetricCard
              index={2}
              accent={STAT_COLORS.success}
              icon={<CheckmarkFilled size={20} />}
              label="Success Rate"
              value={`${formatNumber({
                value: summary.successRate,
                minPrecision: 1,
                maxPrecision: 1,
              })}%`}
              delta={
                <span className={styles.statSub}>
                  {summary.failed} failed of {summary.runs}
                </span>
              }
            />
            <MetricCard
              index={3}
              accent={STAT_COLORS.neutral}
              icon={<Time size={20} />}
              label="Avg Duration"
              value={formatDuration(summary.avgDurationSeconds)}
              delta={
                <span className={styles.statSub}>over {summary.runs} runs</span>
              }
            />
          </MetricGrid>
        </div>
      )}
      <div className={styles.tableArea}>
        <DataTable
          data={data}
          columns={columns as ColumnDef<AgentRun>[]}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          error={error}
          persistenceKey={`agent-cost-runs-${agentType}`}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount: total }}
          initialTableState={{
            sortColumn: 'createdAt',
            sortDirection: 'desc',
            pageSize: PAGE_SIZE,
          }}
          config={{
            columnResizing: true,
            sorting: true,
            globalSearch: false,
            columnFilters: false,
            export: false,
          }}
          onPaginationChange={handlePagination}
          onSortChange={handleSort}
          renderFilterPanel={() => filterControls}
          leftToolbarActions={[
            <Button
              key="histogram-toggle"
              kind="ghost"
              size="md"
              hasIconOnly
              iconDescription={
                histogramOpen
                  ? 'Hide duration distribution'
                  : 'Show duration distribution'
              }
              tooltipPosition="bottom"
              renderIcon={ChartColumn}
              onClick={() => setHistogramOpen((open) => !open)}
            />,
          ]}
          betweenToolbarAndContent={histogramOpen ? histogramPanel : undefined}
        />
      </div>
      <RunDetailSidePanel
        run={selectedRun}
        open={selectedRun !== null}
        onClose={() => setSelectedRun(null)}
      />
      <ProcessStepsSidePanel
        processId={selectedProcessId}
        open={selectedProcessId !== null}
        onClose={() => setSelectedProcessId(null)}
      />
      <AgentConfigSidePanel
        agentType={agentType}
        open={configOpen}
        onClose={() => setConfigOpen(false)}
      />
    </>
  );
};
